/**
 * Entsia `claim.lossType` codes for agent notification emails (must match API FNOL payloads).
 * @module claims/fnolAgentEmailLossType
 */

import type { ClaimFlowDraft } from './claimDraftStorage'

/** Claim branches that send an Entsia loss type on FNOL create. */
export type FnolAgentEmailLossTypeKind =
  | 'windscreen'
  | 'weather'
  | 'theft'
  | 'fire'
  | 'malicious_damage'
  | 'other_loss'
  | 'multi_vehicle'
  | 'single_vehicle'

/** Mirrors {@link api/src/utils/entsiaFnolConstants.ts}. */
const LOSS_TYPE = {
  hailFloodStorm: 'HAILFLOODSTORM',
  theft: 'THEFT',
  attemptedTheft: 'ATTEMPTEDTHEFT',
  fire: 'FIRE',
  maliciousDamage: 'MALICIOUSDAMAGE',
  other: 'OTHER',
  windscreen: 'WINDSCREEN',
  collision: 'COLLISION',
  impactWithAnimal: 'IMPACTWITHANIMAL',
  impactWithObject: 'IMPACTWITHOBJECT',
  impactWithThirdPartyProperty: 'IMPACTTPPROPERTY',
  collisionSingleVehicle: 'COLLISIONSINGLEVEHICLE',
  vehicleCollisionSingleVehicle: 'VEHICLECOLLISIONSINGLEVEHICLE',
} as const

type SingleVehicleTarget = NonNullable<ClaimFlowDraft['singleVehicleCollisionTarget']>

/**
 * Resolves the Entsia loss type code shown on agent emails and sent on FNOL create.
 */
export function resolveAgentEmailLossType(
  draft: ClaimFlowDraft,
  kind: FnolAgentEmailLossTypeKind
): string {
  switch (kind) {
    case 'windscreen':
      return LOSS_TYPE.windscreen
    case 'weather':
      return LOSS_TYPE.hailFloodStorm
    case 'theft':
      return draft.theftVehicleStolenAtIncident === true
        ? LOSS_TYPE.theft
        : LOSS_TYPE.attemptedTheft
    case 'fire':
      return LOSS_TYPE.fire
    case 'malicious_damage':
      return LOSS_TYPE.maliciousDamage
    case 'other_loss':
      return LOSS_TYPE.other
    case 'multi_vehicle':
      return LOSS_TYPE.collision
    case 'single_vehicle':
      return resolveSingleVehicleCollisionLossType(draft.singleVehicleCollisionTarget)
    default: {
      const _exhaustive: never = kind
      return _exhaustive
    }
  }
}

function resolveSingleVehicleCollisionLossType(
  target: ClaimFlowDraft['singleVehicleCollisionTarget']
): string {
  const id = target?.trim() as SingleVehicleTarget | undefined
  if (!id) return LOSS_TYPE.vehicleCollisionSingleVehicle
  switch (id) {
    case 'animal':
      return LOSS_TYPE.impactWithAnimal
    case 'object':
      return LOSS_TYPE.impactWithObject
    case 'third_party_property':
      return LOSS_TYPE.impactWithThirdPartyProperty
    case 'other':
      return LOSS_TYPE.collisionSingleVehicle
    default:
      return LOSS_TYPE.vehicleCollisionSingleVehicle
  }
}
